import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Plus, Search, Trash2, Paperclip } from 'lucide-react';
import AttachmentPanel from '../components/AttachmentPanel';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TIPOS_IMPUESTO = [
  'IVA — Impuesto sobre las Ventas',
  'Retención en la Fuente Nacional',
  'Impuesto de Industria y Comercio (ICA)',
  'Exógena Nacional (Información Exógena DIAN)',
  'Declaración de Renta',
  'Activos en el Exterior',
  'Anticipos RST (Régimen Simple)',
  'RST Consolidado Anual',
  'Exógena Municipal',
  'Impuesto Alumbrado Público — Yumbo',
  'Impuesto al Patrimonio Persona Natural',
  'Registro Único de Beneficiarios Finales (RUB)',
];

const Deadlines = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [deadlines, setDeadlines] = useState([]);
  const [filteredDeadlines, setFilteredDeadlines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState(searchParams.get('estado') || 'Todos');
  const [filterClient, setFilterClient] = useState(searchParams.get('cliente') || '');
  const [filterTaxType, setFilterTaxType] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedDeadline, setSelectedDeadline] = useState(null);
  const [clients, setClients] = useState([]);

  useEffect(() => {
    loadDeadlines();
    loadClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    applyFilters();
  }, [searchTerm, filterStatus, filterClient, filterTaxType, deadlines]);

  const loadDeadlines = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/deadlines`);
      setDeadlines(data);
      setFilteredDeadlines(data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const loadClients = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/clients`);
      setClients(data);
    } catch (error) {
    }
  };

  const updateDeadlineStatus = async (deadlineId, newStatus) => {
    try {
      await axios.put(`${BACKEND_URL}/api/deadlines/${deadlineId}`, { estado: newStatus });
      setDeadlines(prev => prev.map(d => d._id === deadlineId ? { ...d, estado: newStatus } : d));
    } catch (error) {
    }
  };

  const deleteDeadline = async (deadlineId) => {
    if (!window.confirm('¿Está seguro de eliminar este vencimiento?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/deadlines/${deadlineId}`);
      setDeadlines(prev => prev.filter(d => d._id !== deadlineId));
    } catch (error) {
    }
  };

  const applyFilters = () => {
    let filtered = deadlines;

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (d) =>
          d.client_name?.toLowerCase().includes(searchLower) ||
          d.tipo_impuesto?.toLowerCase().includes(searchLower) ||
          d.tipo_display?.toLowerCase().includes(searchLower) ||
          d.periodo_gravable?.toLowerCase().includes(searchLower)
      );
    }

    if (filterStatus !== 'Todos') {
      filtered = filtered.filter((d) => d.estado === filterStatus);
    }

    if (filterClient) {
      filtered = filtered.filter((d) => d.client_id === filterClient);
    }

    if (filterTaxType) {
      filtered = filtered.filter((d) => (d.tipo_display || d.tipo_impuesto) === filterTaxType);
    }

    setFilteredDeadlines(filtered);
  };

  const getStatusBadge = (estado) => {
    const badges = {
      Pendiente: 'bg-indigo-100 text-indigo-800',
      'En proceso': 'bg-blue-100 text-blue-800',
      Realizado: 'bg-emerald-100 text-emerald-800',
      Vencido: 'bg-red-100 text-red-800',
    };
    return badges[estado] || 'bg-slate-100 text-slate-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-600">Cargando vencimientos...</div>
      </div>
    );
  }

  const isRestrictedRole = user?.role === 'Auxiliar Contable' || user?.role === 'Asistente Contable';

  return (
    <div className="space-y-6" data-testid="deadlines-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl tracking-tight font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
            Vencimientos Tributarios
          </h1>
          <p className="text-sm text-slate-700 leading-relaxed mt-2">Gestión de obligaciones fiscales</p>
        </div>
        {!isRestrictedRole && (
          <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
            <DialogTrigger asChild>
              <Button
                className="bg-[#1F3864] hover:bg-[#172b4d] text-white"
                data-testid="create-deadline-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Vencimiento
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Crear Nuevo Vencimiento</DialogTitle>
              </DialogHeader>
              <DeadlineForm onSuccess={() => { setShowCreateModal(false); loadDeadlines(); }} />
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              type="text"
              placeholder="Buscar por período..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-deadlines-input"
            />
          </div>
          <div>
            <select
              value={filterClient}
              onChange={(e) => setFilterClient(e.target.value)}
              className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
              data-testid="filter-client-select"
            >
              <option value="">Todos los clientes</option>
              {clients.map((client) => (
                <option key={client._id} value={client._id}>
                  {client.razon_social || `${client.primer_nombre} ${client.primer_apellido}`}
                </option>
              ))}
            </select>
          </div>
          <div>
            <select
              value={filterTaxType}
              onChange={(e) => setFilterTaxType(e.target.value)}
              className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
              data-testid="filter-tax-type-select"
            >
              <option value="">Todos los impuestos</option>
              {[...new Set(deadlines.map((d) => d.tipo_display || d.tipo_impuesto))].sort().map((tipo) => (
                <option key={tipo} value={tipo}>{tipo}</option>
              ))}
            </select>
          </div>
          <div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
              data-testid="filter-status-select"
            >
              <option value="Todos">Todos los estados</option>
              <option value="Pendiente">Pendiente</option>
              <option value="En proceso">En proceso</option>
              <option value="Realizado">Realizado</option>
              <option value="Vencido">Vencido</option>
            </select>
          </div>
        </div>
      </div>

      {/* Deadlines Table */}
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="text-xs font-semibold tracking-wider uppercase text-slate-500 border-b border-slate-200 bg-slate-50">
              <tr>
                <th className="px-6 py-3">Cliente</th>
                <th className="px-6 py-3">Tipo de Impuesto</th>
                <th className="px-6 py-3">Período Gravable</th>
                <th className="px-6 py-3">Fecha Vencimiento</th>
                <th className="px-6 py-3">Valor Estimado</th>
                <th className="px-6 py-3">Responsable</th>
                <th className="px-6 py-3">Estado</th>
                <th className="px-6 py-3 text-center">Acciones</th>
              </tr>
            </thead>
            <tbody className="text-sm text-slate-700">
              {filteredDeadlines.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-8 text-center text-slate-500">
                    No se encontraron vencimientos
                  </td>
                </tr>
              ) : (
                filteredDeadlines.map((deadline) => (
                  <tr
                    key={deadline._id}
                    className="border-b border-slate-100 hover:bg-slate-50"
                  >
                    <td className="px-6 py-4 font-medium text-slate-900 cursor-pointer hover:text-blue-700"
                      onClick={() => navigate(`/clients/${deadline.client_id}`)}
                      data-testid={`deadline-client-${deadline._id}`}>
                      {deadline.client_name}
                    </td>
                    <td className="px-6 py-4">{deadline.tipo_display || deadline.tipo_impuesto}</td>
                    <td className="px-6 py-4">{deadline.periodo_gravable}</td>
                    <td className="px-6 py-4">{deadline.fecha_vencimiento}</td>
                    <td className="px-6 py-4">
                      {deadline.valor_estimado ? `$${deadline.valor_estimado.toLocaleString()}` : '-'}
                    </td>
                    <td className="px-6 py-4">{deadline.responsable_name}</td>
                    <td className="px-6 py-4">
                      <select
                        value={deadline.estado}
                        onChange={(e) => updateDeadlineStatus(deadline._id, e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        className={`px-2.5 py-1 rounded-full text-xs font-medium border-0 cursor-pointer focus:ring-2 focus:ring-blue-300 ${getStatusBadge(deadline.estado)}`}
                        data-testid={`deadline-status-${deadline._id}`}
                      >
                        <option value="Pendiente">Pendiente</option>
                        <option value="En proceso">En proceso</option>
                        <option value="Realizado">Realizado</option>
                      </select>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={(e) => { e.stopPropagation(); setSelectedDeadline(deadline); }}
                          className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                          title="Adjuntos"
                          data-testid={`deadline-attachments-${deadline._id}`}
                        >
                          <Paperclip className="w-4 h-4" />
                        </button>
                        {!isRestrictedRole && (
                          <button
                            onClick={(e) => { e.stopPropagation(); deleteDeadline(deadline._id); }}
                            className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                            title="Eliminar vencimiento"
                            data-testid={`deadline-delete-${deadline._id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail/Attachments Dialog */}
      <Dialog open={!!selectedDeadline} onOpenChange={(open) => { if (!open) setSelectedDeadline(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detalle del Vencimiento</DialogTitle>
          </DialogHeader>
          {selectedDeadline && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-xs font-semibold tracking-wider uppercase text-slate-500">Cliente</p>
                  <p className="text-slate-900 mt-0.5">{selectedDeadline.client_name}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold tracking-wider uppercase text-slate-500">Tipo de Impuesto</p>
                  <p className="text-slate-900 mt-0.5">{selectedDeadline.tipo_display || selectedDeadline.tipo_impuesto}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold tracking-wider uppercase text-slate-500">Período Gravable</p>
                  <p className="text-slate-900 mt-0.5">{selectedDeadline.periodo_gravable}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold tracking-wider uppercase text-slate-500">Fecha Vencimiento</p>
                  <p className="text-slate-900 mt-0.5">{selectedDeadline.fecha_vencimiento}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold tracking-wider uppercase text-slate-500">Estado</p>
                  <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium mt-0.5 ${getStatusBadge(selectedDeadline.estado)}`}>
                    {selectedDeadline.estado}
                  </span>
                </div>
                <div>
                  <p className="text-xs font-semibold tracking-wider uppercase text-slate-500">Responsable</p>
                  <p className="text-slate-900 mt-0.5">{selectedDeadline.responsable_name}</p>
                </div>
                {selectedDeadline.notas && (
                  <div className="col-span-2">
                    <p className="text-xs font-semibold tracking-wider uppercase text-slate-500">Notas</p>
                    <p className="text-slate-900 mt-0.5">{selectedDeadline.notas}</p>
                  </div>
                )}
              </div>
              <div className="border-t border-slate-200 pt-4">
                <AttachmentPanel entityType="deadline" entityId={selectedDeadline._id} />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Deadline Form Component
const DeadlineForm = ({ onSuccess }) => {
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [allTaxTypes, setAllTaxTypes] = useState(TIPOS_IMPUESTO);
  const [clientTaxConfigs, setClientTaxConfigs] = useState([]);
  const [loadingTaxConfigs, setLoadingTaxConfigs] = useState(false);
  const [formData, setFormData] = useState({
    client_id: '',
    tipo_impuesto: '',
    periodo_gravable: '',
    fecha_vencimiento: '',
    fecha_limite_sancion: '',
    valor_estimado: '',
    responsable_id: '',
    notas: '',
    estado: 'Pendiente',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [periodos, setPeriodos] = useState([]);
  const [alertas, setAlertas] = useState([]);
  const [calculando, setCalculando] = useState(false);
  const [infoCliente, setInfoCliente] = useState(null);

  useEffect(() => {
    loadClients();
    loadUsers();
    loadTaxDefinitions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cuando cambia el cliente, actualizar info tributaria y cargar tax configs
  useEffect(() => {
    if (formData.client_id) {
      const client = clients.find(c => c._id === formData.client_id);
      setInfoCliente(client || null);
      loadClientTaxConfigs(formData.client_id);
      // Reset tipo_impuesto al cambiar cliente
      setFormData(prev => ({ ...prev, tipo_impuesto: '', periodo_gravable: '', fecha_vencimiento: '', fecha_limite_sancion: '' }));
      setPeriodos([]);
      setAlertas([]);
    } else {
      setInfoCliente(null);
      setClientTaxConfigs([]);
      setPeriodos([]);
      setAlertas([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.client_id]);

  // Cuando cambia el tipo de impuesto, calcular periodos
  useEffect(() => {
    if (formData.client_id && formData.tipo_impuesto) {
      calcularPeriodos(formData.client_id, formData.tipo_impuesto);
    } else {
      setPeriodos([]);
      setAlertas([]);
      setFormData(prev => ({ ...prev, periodo_gravable: '', fecha_vencimiento: '', fecha_limite_sancion: '' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.tipo_impuesto]);

  const loadClients = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/clients`);
      setClients(data.filter(c => c.estado === 'Activo'));
    } catch (error) {
    }
  };

  const loadUsers = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/users`);
      setUsers(data);
    } catch (error) {
    }
  };

  const loadTaxDefinitions = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/tax-config/definitions`);
      const names = data.map((d) => d.nombre);
      // Merge with hardcoded list to include all
      const merged = [...new Set([...TIPOS_IMPUESTO, ...names])];
      setAllTaxTypes(merged);
    } catch (error) {
    }
  };

  const loadClientTaxConfigs = async (clientId) => {
    setLoadingTaxConfigs(true);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/clients/${clientId}/tax-config`);
      setClientTaxConfigs(data.filter(c => c.activo !== false));
    } catch (error) {
      setClientTaxConfigs([]);
    } finally {
      setLoadingTaxConfigs(false);
    }
  };

  const calcularPeriodos = async (clientId, tipoImpuesto) => {
    setCalculando(true);
    setAlertas([]);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/utils/calcular-vencimiento`,
        { client_id: clientId, tipo_impuesto: tipoImpuesto },
        {  }
      );
      setPeriodos(data.periodos || []);
      setAlertas(data.alertas || []);

      // Si solo hay 1 periodo (ej: Renta), autoseleccionarlo
      if (data.periodos?.length === 1) {
        const p = data.periodos[0];
        setFormData(prev => ({
          ...prev,
          periodo_gravable: p.periodo,
          fecha_vencimiento: p.fecha_vencimiento,
          fecha_limite_sancion: p.fecha_sancion || '',
        }));
      } else {
        setFormData(prev => ({ ...prev, periodo_gravable: '', fecha_vencimiento: '', fecha_limite_sancion: '' }));
      }
    } catch (error) {
      setAlertas(['Error al calcular los periodos. Ingrese las fechas manualmente.']);
    } finally {
      setCalculando(false);
    }
  };

  const handleSelectPeriodo = (periodo) => {
    const found = periodos.find(p => p.periodo === periodo);
    if (found) {
      setFormData(prev => ({
        ...prev,
        periodo_gravable: found.periodo,
        fecha_vencimiento: found.fecha_vencimiento,
        fecha_limite_sancion: found.fecha_sancion || '',
      }));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const payload = {
        ...formData,
        valor_estimado: formData.valor_estimado ? parseFloat(formData.valor_estimado) : null,
      };

      await axios.post(`${BACKEND_URL}/api/deadlines`, payload);
      onSuccess();
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al crear el vencimiento');
    } finally {
      setLoading(false);
    }
  };

  const getClientName = (client) => {
    return client.razon_social || `${client.primer_nombre} ${client.primer_apellido}`;
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5" data-testid="deadline-form">
      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3">{error}</div>
      )}

      {/* Alertas del cálculo DIAN */}
      {alertas.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-3 space-y-1">
          {alertas.map((alerta, idx) => (
            <p key={idx} className="text-sm text-amber-800 flex items-start gap-2">
              <span className="font-bold text-amber-600 flex-shrink-0">!</span>
              {alerta}
            </p>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Cliente */}
        <div>
          <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500 block mb-2">Cliente *</Label>
          <select name="client_id" value={formData.client_id} onChange={handleChange}
            className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm" required>
            <option value="">Seleccionar cliente...</option>
            {clients.map(client => (
              <option key={client._id} value={client._id}>{getClientName(client)} - NIT {client.nit || client.numero_documento}</option>
            ))}
          </select>
        </div>

        {/* Info tributaria del cliente seleccionado */}
        {infoCliente && (
          <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-xs space-y-1">
            <p className="font-semibold text-slate-700">Perfil tributario:</p>
            {clientTaxConfigs.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {clientTaxConfigs.map(tc => (
                  <span key={tc._id} className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded">
                    {tc.tipo_impuesto.split('—')[0].trim()}{tc.periodicidad ? ` (${tc.periodicidad})` : ''}{tc.municipio ? ` - ${tc.municipio}` : ''}
                  </span>
                ))}
              </div>
            ) : loadingTaxConfigs ? (
              <span className="text-slate-400">Cargando configuración...</span>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {infoCliente.periodicidad_iva && (
                  <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded">IVA {infoCliente.periodicidad_iva}</span>
                )}
                {infoCliente.es_agente_retencion && (
                  <span className="px-2 py-0.5 bg-indigo-100 text-indigo-800 rounded">Agente Retención</span>
                )}
                {infoCliente.declara_renta && (
                  <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 rounded">Declara Renta</span>
                )}
                {infoCliente.regimen_simple && (
                  <span className="px-2 py-0.5 bg-violet-100 text-violet-800 rounded">Rég. Simple</span>
                )}
                {infoCliente.periodicidad_ica && (
                  <span className="px-2 py-0.5 bg-teal-100 text-teal-800 rounded">ICA {infoCliente.periodicidad_ica}</span>
                )}
                {!infoCliente.periodicidad_iva && !infoCliente.es_agente_retencion && !infoCliente.regimen_simple && (
                  <span className="px-2 py-0.5 bg-red-100 text-red-800 rounded">Sin configurar impuestos</span>
                )}
              </div>
            )}
            <p className="text-slate-500">NIT: {infoCliente.nit || infoCliente.numero_documento} | Último dígito: {(infoCliente.nit || infoCliente.numero_documento || '0').replace(/\D/g,'').slice(-1)}</p>
          </div>
        )}

        {/* Tipo de Impuesto - Filtrado por configuración del cliente */}
        <div>
          <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500 block mb-2">Tipo de Impuesto *</Label>
          <select name="tipo_impuesto" value={formData.tipo_impuesto} onChange={handleChange}
            className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm" required disabled={!formData.client_id}
            data-testid="tipo-impuesto-select">
            <option value="">Seleccionar tipo...</option>
            {clientTaxConfigs.length > 0 ? (
              <>
                <optgroup label="Impuestos configurados del cliente">
                  {clientTaxConfigs.map(tc => {
                    const label = tc.municipio
                      ? `${tc.tipo_impuesto} — ${tc.municipio}`
                      : tc.tipo_impuesto;
                    return (
                      <option key={tc._id} value={tc.tipo_impuesto}>{label}{tc.periodicidad ? ` (${tc.periodicidad})` : ''}</option>
                    );
                  })}
                </optgroup>
                <optgroup label="Otros impuestos">
                  {allTaxTypes.filter(tipo => !clientTaxConfigs.some(tc => tc.tipo_impuesto === tipo)).map(tipo => (
                    <option key={tipo} value={tipo}>{tipo}</option>
                  ))}
                </optgroup>
              </>
            ) : (
              allTaxTypes.map(tipo => (
                <option key={tipo} value={tipo}>{tipo}</option>
              ))
            )}
          </select>
          {formData.client_id && clientTaxConfigs.length === 0 && !loadingTaxConfigs && (
            <p className="text-xs text-slate-400 mt-1">Este cliente no tiene impuestos configurados. Configúrelos en la ficha del cliente.</p>
          )}
        </div>

        {/* Periodo Gravable - Auto calculado */}
        <div>
          <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500 block mb-2">
            Período Gravable *
            {calculando && <span className="ml-2 text-blue-500 text-[10px] font-normal">Calculando...</span>}
          </Label>
          {periodos.length > 0 ? (
            <select
              value={formData.periodo_gravable}
              onChange={(e) => handleSelectPeriodo(e.target.value)}
              className="flex h-10 w-full rounded-md border border-blue-300 bg-blue-50 px-3 py-2 text-sm font-medium text-blue-900"
              required
              data-testid="periodo-gravable-select"
            >
              <option value="">Seleccionar periodo...</option>
              {periodos.map(p => (
                <option key={p.periodo} value={p.periodo}>{p.periodo} (Vence: {p.fecha_vencimiento})</option>
              ))}
            </select>
          ) : (
            <Input
              name="periodo_gravable"
              value={formData.periodo_gravable}
              onChange={handleChange}
              required
              placeholder="Ej: Enero 2026, Bimestre 1 (Ene-Feb 2026)"
              data-testid="periodo-gravable-input"
            />
          )}
        </div>

        {/* Fecha de Vencimiento */}
        <div>
          <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500 block mb-2">
            Fecha de Vencimiento *
            {formData.fecha_vencimiento && periodos.length > 0 && (
              <span className="ml-2 text-emerald-600 text-[10px] font-normal">DIAN Auto</span>
            )}
          </Label>
          <Input
            type="date"
            name="fecha_vencimiento"
            value={formData.fecha_vencimiento}
            onChange={handleChange}
            required
            className={periodos.length > 0 && formData.fecha_vencimiento ? 'border-emerald-300 bg-emerald-50' : ''}
          />
        </div>

        {/* Fecha Límite con Sanción */}
        <div>
          <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500 block mb-2">
            Fecha Límite con Sanción
            {formData.fecha_limite_sancion && periodos.length > 0 && (
              <span className="ml-2 text-emerald-600 text-[10px] font-normal">DIAN Auto</span>
            )}
          </Label>
          <Input
            type="date"
            name="fecha_limite_sancion"
            value={formData.fecha_limite_sancion}
            onChange={handleChange}
            className={periodos.length > 0 && formData.fecha_limite_sancion ? 'border-emerald-300 bg-emerald-50' : ''}
          />
        </div>

        {/* Valor Estimado */}
        <div>
          <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500 block mb-2">Valor Estimado (COP)</Label>
          <Input type="number" name="valor_estimado" value={formData.valor_estimado} onChange={handleChange} placeholder="0" />
        </div>

        {/* Responsable */}
        <div>
          <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500 block mb-2">Responsable *</Label>
          <select name="responsable_id" value={formData.responsable_id} onChange={handleChange}
            className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm" required>
            <option value="">Seleccionar responsable...</option>
            {users.map(usr => (
              <option key={usr._id} value={usr._id}>{usr.name} - {usr.role}</option>
            ))}
          </select>
        </div>

        {/* Estado */}
        <div>
          <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500 block mb-2">Estado</Label>
          <select name="estado" value={formData.estado} onChange={handleChange}
            className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm">
            <option value="Pendiente">Pendiente</option>
            <option value="En proceso">En proceso</option>
            <option value="Realizado">Realizado</option>
          </select>
        </div>

        {/* Notas */}
        <div className="md:col-span-2">
          <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500 block mb-2">Notas u Observaciones</Label>
          <textarea
            name="notas"
            value={formData.notas}
            onChange={handleChange}
            className="flex min-h-[80px] w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
            placeholder="Información adicional sobre el vencimiento..."
          />
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t">
        <Button type="submit" disabled={loading} className="bg-[#1F3864] hover:bg-[#172b4d] text-white" data-testid="submit-deadline-form">
          {loading ? 'Creando...' : 'Crear Vencimiento'}
        </Button>
      </div>
    </form>
  );
};

export default Deadlines;
